import { TableData } from "../types/table-filter-types";

export interface TableFilters {
  searchSubstring?: string;
  statuses?: string[];
  landuses?: string[];
  plotArea?: { min?: number; max?: number };
  formationDate?: { min?: number; max?: number };
}

type FilterTableData = (
  data: TableData,
  filters: TableFilters,
  currentPage: number,
  pageSize: number,
) => { data: TableData; currentPage: number; totalDataSize: number };

type FilterBy = (
  data: TableData,
  currentFilters: TableFilters,
  currentPage: number,
  pageSize: number,
) => TableData;

const getPage: FilterBy = (data, currentFilters, currentPage, pageSize) => {
  let page = data.length <= pageSize * (currentPage - 1) ? Math.ceil(data.length / pageSize) : currentPage
  return data.slice((page! - 1) * pageSize!, page! * pageSize!);
};

const filterByFormationDate: FilterBy = (data, currentFilters, currentPage, pageSize) => {
  let res = data;

  if (currentFilters.formationDate) {
    if (
      currentFilters.formationDate.min &&
      new Date(currentFilters.formationDate.min)
    ) {
      const minInDate = new Date(currentFilters.formationDate.min);
      res = res.filter((row) => {
        return new Date(row.changedDateTime) >= minInDate;
      });
    }
    if (
      currentFilters.formationDate.max &&
      new Date(currentFilters.formationDate.max)
    ) {
      const maxInDate = new Date(currentFilters.formationDate.max);
      res = res.filter((row) => {
        return new Date(row.changedDateTime) <= maxInDate;
      });
    }
  }

  return res;
};

const filterByPlotArea: FilterBy = (data, currentFilters, currentPage, pageSize) => {
  let res = data;

  if (currentFilters.plotArea) {
    if (currentFilters.plotArea.min) {
      res = res.filter((row) => row.area >= currentFilters.plotArea!.min!);
    }
    if (currentFilters.plotArea.max) {
      res = res.filter((row) => row.area <= currentFilters.plotArea!.max!);
    }
  }

  return filterByFormationDate(res, currentFilters, currentPage, pageSize);
};

const filterByLanduses: FilterBy = (data, currentFilters, currentPage, pageSize) => {
  let res = data;

  if (currentFilters.landuses) {
    res = res.filter((row) =>
      currentFilters.landuses?.includes(row.landUse.toLowerCase()),
    );
  }

  return filterByPlotArea(res, currentFilters, currentPage, pageSize);
};

const filterByStatuses: FilterBy = (data, currentFilters, currentPage, pageSize) => {
  let res = data;

  if (currentFilters.statuses) {
    res = res.filter((row) =>
      currentFilters.statuses?.includes(row.status.toLowerCase()),
    );
  }

  return filterByLanduses(res, currentFilters, currentPage, pageSize);
};

export const filterBySubstring = (
    data: TableData,
    currentFilters: TableFilters,
    currentPage: number,
    pageSize: number,
    substringFields: string[],
): TableData => {
  let res = data;

  if (currentFilters.searchSubstring) {
    const searchStr = currentFilters.searchSubstring.toLowerCase();
    res = res.filter((row) => {
      return substringFields.some((field) => {
        const value = row[field];
        return typeof value === 'string' && value.toLowerCase().includes(searchStr);
      });
    });
  }

  return filterByStatuses(res, currentFilters, currentPage, pageSize);
};


export const filterTableData = (
    data: TableData,
    filters: TableFilters,
    currentPage: number,
    pageSize: number,
    substringFields: string[]
) => {
  const result = filterBySubstring(data, filters, currentPage, pageSize, substringFields)
  const pagedResult = getPage(result, filters, currentPage, pageSize)
  const totalDataSize = result.length;
  const maxPage = Math.max(1, Math.ceil(totalDataSize / pageSize));
  const safePage = Math.min(currentPage, maxPage);
  return {
    data: pagedResult,
    currentPage: safePage,
    totalDataSize: result.length,
  };
};
